/**
 * EEG–PACS / multimodal imaging linkage (Epic D3 / Gap #17).
 * Patient-level ImagingStudy / DICOM refs linked to EEG study context,
 * event↔slice associations for co-review, and tumor-board export packages.
 */

export const EEG_IMAGING_LINKAGE_SCHEMA_VERSION = "eeg-imaging-linkage-v1.0.0";

export type ImagingSource = "pacs" | "fhir" | "local";

export type ImagingStudyRef = {
  studyInstanceUID: string;
  seriesInstanceUID?: string | null;
  modality?: string | null;
  description?: string | null;
  source: ImagingSource;
  patientId: string;
  accessionNumber?: string | null;
  viewerUrl?: string | null;
};

export type EegImagingEventAssociation = {
  associationId: string;
  eegEventId: string;
  timeSeconds: number;
  studyInstanceUID: string;
  seriesInstanceUID?: string | null;
  frameNumber?: number | null;
  note?: string | null;
};

export type EegImagingLinkBundle = {
  schemaVersion: string;
  eegStudyId: string;
  patientId: string;
  imaging: ImagingStudyRef[];
  associations: EegImagingEventAssociation[];
  patientMatchOk: boolean;
  patientMatchWarning: string | null;
  accessAudit: ImagingAccessAuditEntry[];
};

export type ImagingAccessAuditEntry = {
  atIso: string;
  actorId: string;
  action: "list" | "open" | "associate" | "export" | "deny";
  eegStudyId: string;
  studyInstanceUID?: string | null;
  reason?: string | null;
};

export type TumorBoardExportPackage = {
  packageId: string;
  createdAtIso: string;
  eegStudyId: string;
  patientId: string;
  eegSnapshotRefs: Array<{ evidenceId: string; label: string; timeSeconds?: number }>;
  imagingRefs: ImagingStudyRef[];
  associations: EegImagingEventAssociation[];
  retentionClass: "multimodal_phi_conference";
  accessAudit: ImagingAccessAuditEntry;
};

let associationSeq = 0;
let packageSeq = 0;

export function assertPatientMatch(
  eegPatientId: string,
  imagingPatientId: string,
): { ok: boolean; warning: string | null } {
  const a = eegPatientId.trim().toLowerCase();
  const b = imagingPatientId.trim().toLowerCase();
  if (!a || !b) {
    return { ok: false, warning: "Missing patient identifier for imaging match" };
  }
  if (a !== b) {
    return {
      ok: false,
      warning: `Patient mismatch: EEG=${eegPatientId} vs imaging=${imagingPatientId}`,
    };
  }
  return { ok: true, warning: null };
}

export function linkImagingToEegStudy(input: {
  eegStudyId: string;
  patientId: string;
  imaging: ImagingStudyRef[];
  actorId: string;
  now?: Date;
}): EegImagingLinkBundle {
  const mismatches = input.imaging.filter(
    (img) => !assertPatientMatch(input.patientId, img.patientId).ok,
  );
  const patientMatchOk = mismatches.length === 0;
  const warning = patientMatchOk
    ? null
    : mismatches.map((m) => assertPatientMatch(input.patientId, m.patientId).warning).join("; ");

  const atIso = (input.now ?? new Date()).toISOString();
  return {
    schemaVersion: EEG_IMAGING_LINKAGE_SCHEMA_VERSION,
    eegStudyId: input.eegStudyId,
    patientId: input.patientId,
    imaging: patientMatchOk ? input.imaging : input.imaging.filter((img) =>
      assertPatientMatch(input.patientId, img.patientId).ok,
    ),
    associations: [],
    patientMatchOk,
    patientMatchWarning: warning,
    accessAudit: [
      {
        atIso,
        actorId: input.actorId,
        action: patientMatchOk ? "list" : "deny",
        eegStudyId: input.eegStudyId,
        reason: warning,
      },
    ],
  };
}

export function associateEegEventToImaging(
  bundle: EegImagingLinkBundle,
  event: { eventId: string; timeSeconds: number; note?: string | null },
  imagingRef: {
    studyInstanceUID: string;
    seriesInstanceUID?: string | null;
    frameNumber?: number | null;
  },
  actorId: string,
  now?: Date,
): EegImagingLinkBundle {
  const exists = bundle.imaging.some(
    (img) => img.studyInstanceUID === imagingRef.studyInstanceUID,
  );
  if (!exists) {
    return {
      ...bundle,
      accessAudit: [
        ...bundle.accessAudit,
        {
          atIso: (now ?? new Date()).toISOString(),
          actorId,
          action: "deny",
          eegStudyId: bundle.eegStudyId,
          studyInstanceUID: imagingRef.studyInstanceUID,
          reason: "imaging_not_in_bundle",
        },
      ],
    };
  }
  associationSeq += 1;
  const association: EegImagingEventAssociation = {
    associationId: `assoc-${associationSeq}`,
    eegEventId: event.eventId,
    timeSeconds: event.timeSeconds,
    studyInstanceUID: imagingRef.studyInstanceUID,
    seriesInstanceUID: imagingRef.seriesInstanceUID ?? null,
    frameNumber: imagingRef.frameNumber ?? null,
    note: event.note ?? null,
  };
  return {
    ...bundle,
    associations: [...bundle.associations, association],
    accessAudit: [
      ...bundle.accessAudit,
      {
        atIso: (now ?? new Date()).toISOString(),
        actorId,
        action: "associate",
        eegStudyId: bundle.eegStudyId,
        studyInstanceUID: imagingRef.studyInstanceUID,
      },
    ],
  };
}

export function buildTumorBoardExportPackage(input: {
  bundle: EegImagingLinkBundle;
  eegSnapshotRefs: Array<{ evidenceId: string; label: string; timeSeconds?: number }>;
  actorId: string;
  now?: Date;
}): TumorBoardExportPackage {
  packageSeq += 1;
  const atIso = (input.now ?? new Date()).toISOString();
  return {
    packageId: `tumor-board-${packageSeq}`,
    createdAtIso: atIso,
    eegStudyId: input.bundle.eegStudyId,
    patientId: input.bundle.patientId,
    eegSnapshotRefs: input.eegSnapshotRefs,
    imagingRefs: input.bundle.imaging,
    associations: input.bundle.associations,
    retentionClass: "multimodal_phi_conference",
    accessAudit: {
      atIso,
      actorId: input.actorId,
      action: "export",
      eegStudyId: input.bundle.eegStudyId,
    },
  };
}

export function resolveImagingViewerTarget(
  bundle: EegImagingLinkBundle,
  associationId: string,
): { ok: boolean; viewerUrl: string | null; association: EegImagingEventAssociation | null } {
  const association =
    bundle.associations.find((a) => a.associationId === associationId) ?? null;
  if (!association) return { ok: false, viewerUrl: null, association: null };
  const imaging = bundle.imaging.find(
    (img) => img.studyInstanceUID === association.studyInstanceUID,
  );
  const viewerUrl =
    imaging?.viewerUrl ||
    `/imaging/viewer?studyUID=${encodeURIComponent(association.studyInstanceUID)}${
      association.seriesInstanceUID
        ? `&seriesUID=${encodeURIComponent(association.seriesInstanceUID)}`
        : ""
    }${
      association.frameNumber != null ? `&frame=${association.frameNumber}` : ""
    }`;
  return { ok: true, viewerUrl, association };
}

/** Multimodal PHI retention / privacy controls (EEG-T20-17-004). */
export const MULTIMODAL_PHI_CONTROLS = {
  retentionDaysConferenceExport: 90,
  requirePatientMatch: true,
  auditAllAccess: true,
  forbidPublicLinks: true,
  riskId: "NDX-RISK-EEG-MULTIMODAL-PHI-001",
};

/**
 * CX-G12 / S5 — live PACS/DICOM co-registration session for EEG review.
 * Links imaging refs, optional BEM geometry URI, and event↔slice associations.
 */
export type PacsCoregSession = {
  schemaVersion: string;
  sessionId: string;
  eegStudyId: string;
  patientId: string;
  bundle: EegImagingLinkBundle;
  bemGeometryUri: string | null;
  geometryMode: "template_mri" | "patient_specific" | "unknown";
  dicomweb: {
    qidoReady: boolean;
    wadoReady: boolean;
    stowReady: boolean;
  };
  readyForEsi: boolean;
  warnings: string[];
};

export function buildPacsCoregSession(input: {
  eegStudyId: string;
  patientId: string;
  imaging: ImagingStudyRef[];
  actorId: string;
  bemGeometryUri?: string | null;
  dicomweb?: Partial<PacsCoregSession["dicomweb"]>;
  now?: Date;
}): PacsCoregSession {
  const bundle = linkImagingToEegStudy({
    eegStudyId: input.eegStudyId,
    patientId: input.patientId,
    imaging: input.imaging,
    actorId: input.actorId,
    now: input.now,
  });
  const warnings: string[] = [];
  if (!bundle.patientMatchOk) {
    warnings.push(bundle.patientMatchWarning || "patient_match_failed");
  }
  if (!bundle.imaging.length) warnings.push("no_imaging_studies_linked");
  const hasBem = Boolean(input.bemGeometryUri);
  const dicomweb = {
    qidoReady: input.dicomweb?.qidoReady ?? true,
    wadoReady: input.dicomweb?.wadoReady ?? true,
    stowReady: input.dicomweb?.stowReady ?? false,
  };
  if (!dicomweb.qidoReady || !dicomweb.wadoReady) {
    warnings.push("dicomweb_query_retrieve_incomplete");
  }
  return {
    schemaVersion: EEG_IMAGING_LINKAGE_SCHEMA_VERSION,
    sessionId: `pacs-coreg-${input.eegStudyId}`,
    eegStudyId: input.eegStudyId,
    patientId: input.patientId,
    bundle,
    bemGeometryUri: input.bemGeometryUri ?? null,
    geometryMode: hasBem ? "patient_specific" : "template_mri",
    dicomweb,
    readyForEsi: bundle.patientMatchOk && bundle.imaging.length > 0,
    warnings,
  };
}
